import React, {useState} from 'react';
import Modal from "../../components/modal/modal";
import axiosInstance from "../../AxiosInstance";
import {DangerButton, FileInput} from "../../components/input/inputs";
import styles from "../../styles/main.module.css";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCloudArrowUp} from "@fortawesome/free-solid-svg-icons";
import { purchaseOrderListHandlersRef } from '../bus/purchaseOrderListHandlersRef';
import { openNecDetail, openSuccessUpload } from '../bus/modalApi';
import { toast, ToastTypes } from '../../components/toasts/toast/toast';
import premium from '../invoice_detail/invoiceDetailModal.module.css';
import upStyles from './uploadModal.module.css';
import { purchaseOrderInvoiceAmountLeft } from '../../utils/currency';

function MetaRelated({ docLabel, numberLabel }) {
    const showNumero = numberLabel !== undefined && numberLabel !== null;
    return (
        <div className={upStyles.metaBlock}>
            <div className={upStyles.metaRow}>
                <span className={upStyles.metaLabel}>Tip. Doc. relacionado</span>
                <span className={`${upStyles.metaValue} ${upStyles.metaPill}`}>{docLabel}</span>
            </div>
            {showNumero && (
                <div className={upStyles.metaRow}>
                    <span className={upStyles.metaLabel}>Nro. Doc. relacionado</span>
                    <span className={`${upStyles.metaValue} ${upStyles.metaPill}`}>{numberLabel}</span>
                </div>
            )}
        </div>
    );
}

function RelatedInvoiceSelector({ options, value, onChange }) {
    return (
        <div className={upStyles.metaRow}>
            <label className={upStyles.metaLabel} htmlFor="related-bill-id">
                Nro. Doc. relacionado
            </label>
            <select
                id="related-bill-id"
                className={upStyles.metaSelect}
                value={value ?? ''}
                onChange={(event) => onChange(event.target.value)}
            >
                <option value="">Subir nuevo comprobante por XML</option>
                {options.map((option) => (
                    <option key={option.id} value={option.id}>
                        {option.label}
                    </option>
                ))}
            </select>
        </div>
    );
}

function rejectFailedResponse(response) {
    const status = response?.status ?? response?.response?.status;
    if (status >= 400) {
        return Promise.reject(response?.response ? response : { response });
    }
    return response;
}

function uploadErrorMessage(err) {
    const detail = err.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) {
        return detail;
    }
    if (typeof err.response?.data === 'string' && err.response.data.trim()) {
        return err.response.data;
    }
    return 'No se pudieron guardar los archivos.';
}

export default function UploadModal({show, setter, oc, currentInvoiceName, currentNEC, userType}) {
    const [form, setForm] = useState({});
    const necBills = (oc?.bills || []).filter((x) => x.order_detail?.id === currentNEC?.id);
    const invoiceOptions = necBills.map((x) => ({
        id: String(x.id),
        label: x.sn || String(x.id),
    }));

    const uploadFiles = () => {
        if (currentNEC?.id == null) {
            toast('Falta la NEC asociada. Vuelva a abrir el detalle e intente de nuevo.', ToastTypes.ERROR);
            return;
        }
        const selectedBillId = form.bill_id;
        const hasInvoiceXml = !!form.xml;
        if (!selectedBillId && !hasInvoiceXml) {
            toast('Seleccione un comprobante asociado o adjunte el XML del comprobante.', ToastTypes.ERROR);
            return;
        }
        if (selectedBillId && hasInvoiceXml) {
            toast('Para cargar un nuevo comprobante XML no seleccione un comprobante existente.', ToastTypes.ERROR);
            return;
        }
        let formData = new FormData();
        for(const key in form) {
            if (form[key] !== undefined && form[key] !== null && form[key] !== '') {
                formData.append(key, form[key]);
            }
        }
        formData.append("order_detail_id", currentNEC.id);

        axiosInstance.post('bill/', formData).then(rejectFailedResponse).then(() => {
            setter(false);
            axiosInstance.get(`purchase_order/${oc.id}/`).then(rejectFailedResponse).then((response) => {
                const h = purchaseOrderListHandlersRef.current;
                if (!h) {
                    window.location.reload();
                    return;
                }
                const nextOc = {
                    ...response.data,
                    amount_left: purchaseOrderInvoiceAmountLeft(response.data),
                };
                h.replacePurchaseOrder?.(nextOc);
                const foundNEC = nextOc.order_details?.filter((x) => x?.id === currentNEC?.id) ?? [];
                if (foundNEC.length > 0) {
                    const foundBills = nextOc.bills?.filter(
                        (x) => x.order_detail?.id === foundNEC[0]?.id
                    ) ?? [];
                    const selectedBillId = form.bill_id ? Number(form.bill_id) : null;
                    const pinBill =
                        selectedBillId
                            ? foundBills.find((x) => x.id === selectedBillId)
                            : foundBills.length === 1
                            ? foundBills[0]
                            : foundBills.reduce(
                                  (best, cur) =>
                                      !best || cur.id > best.id ? cur : best,
                                  null
                              );
                    openNecDetail({
                        oc: nextOc,
                        nec: foundNEC[0],
                        bill: pinBill || null,
                        userType: h.userType || userType,
                    });
                }
                setForm({});
                openSuccessUpload();
            }).catch((err) => {
                console.error(err);
                toast('No se pudo actualizar la lista de órdenes.', ToastTypes.ERROR);
            });
        }).catch((err) => {
            console.error(err);
            toast(uploadErrorMessage(err), ToastTypes.ERROR);
        });
    }

    return (
        <Modal
            show={show}
            setter={setter}
            size={'xl'}
            name={'Subir Documento'}
            hideClose={true}
            headerClassName={premium.headerPremium}
            titleClassName={premium.titlePremium}
            dialogClassName={upStyles.uploadModalDialog}
        >
            <div className={upStyles.uploadContent}>
                <div className={upStyles.dropZoneBanner}>
                    <FontAwesomeIcon icon={faCloudArrowUp} className={upStyles.dropZoneIcon} />
                    <div>
                        <strong>Arrastrar y soltar</strong>
                        {' '}
                        — puede soltar cada archivo en el recuadro punteado que corresponda, o hacer clic en la zona del input para elegirlo.
                    </div>
                </div>

                <div className={upStyles.metaBlock}>
                    <div className={upStyles.metaRow}>
                        <span className={upStyles.metaLabel}>Tip. Doc. relacionado</span>
                        <span className={`${upStyles.metaValue} ${upStyles.metaPill}`}>Comprobante</span>
                    </div>
                    <RelatedInvoiceSelector
                        options={invoiceOptions}
                        value={form.bill_id}
                        onChange={(value) => {
                            setForm((prev) => ({
                                ...prev,
                                bill_id: value,
                            }));
                        }}
                    />
                </div>

                {oc.type === 1 &&
                    <div className={upStyles.fileSection}>
                        <div className={upStyles.sectionSubtitle}>Archivos — servicio</div>
                        <div className={upStyles.fileFieldsGrid}>
                        <FileInput comfortable form={form} setForm={setForm} name={'Seleccionar comprobante PDF'} accept={'.pdf'}
                                   onChange={(event) => {
                                       setForm({
                                           ...form,
                                           "pdf": event.target.files[0]
                                       });
                                   }}/>

                        <FileInput comfortable form={form} setForm={setForm} name={'Seleccionar comprobante XML'} accept={'.xml'}
                                   onChange={(event) => {
                                       setForm({
                                           ...form,
                                           "xml": event.target.files[0]
                                       });
                                   }}/>

                        <FileInput comfortable
                            form={form}
                            setForm={setForm}
                            name={'CDR constancia (.zip) — arrastrar o clic'}
                            internalName={'cdr'}
                            accept={'.zip,application/zip'}
                            onChange={(event) => {
                                const f = event.target.files?.[0];
                                setForm((prev) => ({
                                    ...prev,
                                    cdr: f,
                                }));
                            }}
                        />

                        <FileInput comfortable form={form} setForm={setForm} name={'Seleccionar acta de conformidad PDF'}
                                   accept={'.pdf'} onChange={(event) => {
                            setForm({
                                ...form,
                                "pdf_guide": event.target.files[0]
                            });
                        }}/>

                        <FileInput comfortable form={form} setForm={setForm} name={'Seleccionar informe de servicio PDF'}
                                   accept={'.pdf'} onChange={(event) => {
                            setForm({
                                ...form,
                                "other_pdf": event.target.files[0]
                            });
                        }}/>

                        <FileInput comfortable form={form} setForm={setForm} name={'Otro Archivo'} accept={'.*'} onChange={(event) => {
                            setForm({
                                ...form,
                                "other_guide_2": event.target.files[0]
                            });
                        }}/>
                        </div>
                    </div>
                }

                {oc.type === 0 &&
                    <div className={upStyles.fileSection}>
                        <div className={upStyles.sectionSubtitle}>Archivos — suministro</div>
                        <div className={upStyles.fileFieldsGrid}>
                        <FileInput comfortable form={form} setForm={setForm} name={'Seleccionar archivo PDF'} accept={'.pdf'}
                                   onChange={(event) => {
                                       setForm({
                                           ...form,
                                           "pdf": event.target.files[0]
                                       });
                                   }}
                        />

                        <FileInput comfortable form={form} setForm={setForm} name={'Seleccionar archivo XML'} accept={'.xml'}
                                   onChange={(event) => {
                                       setForm({
                                           ...form,
                                           "xml": event.target.files[0]
                                       });
                                   }}
                        />

                        <FileInput comfortable
                            form={form}
                            setForm={setForm}
                            name={'CDR constancia (.zip) — arrastrar o clic'}
                            internalName={'cdr'}
                            accept={'.zip,application/zip'}
                            onChange={(event) => {
                                const f = event.target.files?.[0];
                                setForm((prev) => ({
                                    ...prev,
                                    cdr: f,
                                }));
                            }}
                        />

                        <FileInput comfortable form={form} setForm={setForm} name={'Otro archivo PDF'} accept={'.pdf'}
                                   onChange={(event) => {
                                       setForm({
                                           ...form,
                                           "other_pdf": event.target.files[0]
                                       });
                                   }}
                        />
                        </div>
                    </div>
                }

                {oc.type === 0 &&
                    <div className={upStyles.guideSection}>
                        <div className={upStyles.sectionSubtitle}>Guía de remisión</div>
                        <MetaRelated docLabel="Guía de Remisión" />

                        <div className={upStyles.fileFieldsGrid}>
                            <FileInput comfortable form={form} setForm={setForm} name={'Seleccionar archivo XML'} accept={'.xml'}
                                       onChange={(event) => {
                                           setForm({
                                               ...form,
                                               "xml_guide": event.target.files[0]
                                           });
                                       }}/>

                            <FileInput comfortable form={form} setForm={setForm} name={'Seleccionar archivo PDF'} accept={'.pdf'}
                                       onChange={(event) => {
                                           setForm({
                                               ...form,
                                               "pdf_guide": event.target.files[0]
                                           });
                                       }}/>

                            <FileInput comfortable form={form} setForm={setForm} name={'Otro Archivo'} accept={'.*'} onChange={(event) => {
                                setForm({
                                    ...form,
                                    "other_guide_1": event.target.files[0]
                                });
                            }}/>

                            <FileInput comfortable form={form} setForm={setForm} name={'Otro Archivo'} accept={'.*'} onChange={(event) => {
                                setForm({
                                    ...form,
                                    "other_guide_2": event.target.files[0]
                                });
                            }}/>
                        </div>
                    </div>
                }
            </div>

            <div className={upStyles.footerBar}>
                <div className={upStyles.footerActions}>
                    <div onClick={() => uploadFiles()} className={styles.modalButton}
                         style={{backgroundColor: "#4A4DBB"}} role="button" tabIndex={0}
                         onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') uploadFiles(); }}>
                        Guardar
                    </div>
                    <DangerButton onClick={() => setter(false)} name={'Cerrar'}/>
                </div>
            </div>
        </Modal>
    )
}
